// FURPPL logo autoscroll (2026-10-25, founder: "Our Brands, make autoscroll"), for sections/logo-list.liquid. The CSS
// moves the line; this adds what CSS cannot: one speed (pixels per second) on every screen width, drag or swipe it, a
// button that pauses it, keyboard focus brings a logo into view, and the logos load before they slide in.
(() => {
  if (window.customElements.get('furppl-marquee')) return;

  class FurpplMarquee extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.view = this.querySelector('.furppl-marquee__view');
      this.track = this.querySelector('.furppl-marquee__track');
      this.toggle = this.querySelector('.furppl-marquee__toggle');
      this.anim = this.track && this.track.getAnimations()[0];
      // reduced motion: the CSS gives the line no animation, so it scrolls by hand and the button stays hidden
      if (!this.view || !this.anim) return;
      this.ready = true;
      this.speed = Number(this.dataset.speed) || 50;
      this.held = new Set(); // what holds it still: 'user' (the button), 'hover', 'focus', 'drag'
      this.fit();
      new ResizeObserver(() => this.fit()).observe(this.view);
      this.loadSoon();
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => this.hold('user', !this.held.has('user')));
      }
      this.view.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') this.hold('hover', true);
      });
      this.view.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') this.hold('hover', false);
      });
      this.view.addEventListener('focusin', (e) => {
        this.hold('focus', true);
        this.reveal(e.target);
      });
      this.view.addEventListener('focusout', (e) => {
        if (this.view.contains(e.relatedTarget)) return;
        this.view.scrollLeft = 0;
        this.hold('focus', false);
      });
      this.view.addEventListener('pointerdown', (e) => this.down(e));
      this.view.addEventListener('pointermove', (e) => this.move(e));
      this.view.addEventListener('pointerup', (e) => this.up(e));
      this.view.addEventListener('pointercancel', (e) => this.up(e));
      this.view.addEventListener('dragstart', (e) => e.preventDefault());
      // a drag is not a tap: the logo under the pointer does not open
      this.view.addEventListener('click', (e) => {
        if (!this.dragged) return;
        e.preventDefault();
        e.stopPropagation();
        this.dragged = false;
      }, true);
    }

    duration() {
      return this.anim.effect.getComputedTiming().duration;
    }

    // one loop = half the track (the logos are there twice), timed so the line moves this.speed pixels a second
    fit() {
      const half = this.track.offsetWidth / 2;
      if (!half) return;
      const ms = (half / this.speed) * 1000;
      const old = this.duration();
      if (Math.abs(old - ms) < 1) return;
      const at = ((this.anim.currentTime || 0) / old) % 1;
      this.anim.effect.updateTiming({ duration: ms });
      this.anim.currentTime = at * ms;
    }

    hold(why, on) {
      if (on) this.held.add(why);
      else this.held.delete(why);
      if (this.held.size) this.anim.pause();
      else this.anim.play();
      const user = this.held.has('user');
      this.classList.toggle('is-paused', user);
      if (this.toggle) this.toggle.setAttribute('aria-label', user ? this.toggle.dataset.play : this.toggle.dataset.pause);
    }

    // keyboard: the focused logo slides into view, a little in from the faded edge
    reveal(el) {
      const item = el && el.closest && el.closest('.furppl-marquee__item');
      if (!item) return;
      const half = this.track.offsetWidth / 2;
      const x = item.getBoundingClientRect().left - this.track.getBoundingClientRect().left - this.view.clientWidth * 0.1;
      this.anim.currentTime = ((((x % half) + half) % half) / half) * this.duration();
      this.view.scrollLeft = 0;
      requestAnimationFrame(() => {
        this.view.scrollLeft = 0;
      });
    }

    down(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.dragged = false;
      this.drag = { id: e.pointerId, x: e.clientX, t: this.anim.currentTime || 0, half: this.track.offsetWidth / 2, d: this.duration(), on: false };
    }

    move(e) {
      const g = this.drag;
      if (!g || e.pointerId !== g.id) return;
      const dx = e.clientX - g.x;
      if (!g.on) {
        if (Math.abs(dx) < 6) return;
        g.on = true;
        this.dragged = true;
        this.hold('drag', true);
        try {
          this.view.setPointerCapture(g.id);
        } catch (err) {
          // the pointer is already gone
        }
      }
      this.anim.currentTime = (((g.t - (dx / g.half) * g.d) % g.d) + g.d) % g.d;
    }

    up(e) {
      const g = this.drag;
      if (!g || e.pointerId !== g.id) return;
      this.drag = null;
      if (!g.on) return;
      // after a swipe the line waits a moment before it moves on
      if (e.pointerType === 'mouse') this.hold('drag', false);
      else setTimeout(() => this.hold('drag', false), 1200);
    }

    // the logos load just before the line comes into view, so none pops in as it slides on
    loadSoon() {
      const imgs = [...this.querySelectorAll('img[loading="lazy"]')];
      if (!imgs.length || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((x) => x.isIntersecting)) return;
        io.disconnect();
        imgs.forEach((img) => {
          img.loading = 'eager';
        });
      }, { rootMargin: '600px 0px' });
      io.observe(this);
    }
  }

  window.customElements.define('furppl-marquee', FurpplMarquee);
})();
